import { maxInlineOperatorChartDescriptionBytes } from "../constants";
import type { HighchartsWrapperProps, OperatorChartDataAccess, OperatorTimeSeriesSummary } from "../types";
import { getOperatorTimeSeriesData } from "./getOperatorTimeSeriesData";

const getUtf8ByteLength = (value: string): number => {
  let byteLength = 0;

  for (const character of value) {
    const codePoint = character.codePointAt(0) || 0;

    if (codePoint <= 0x7f) {
      byteLength += 1;
    } else if (codePoint <= 0x7ff) {
      byteLength += 2;
    } else if (codePoint <= 0xffff) {
      byteLength += 3;
    } else {
      byteLength += 4;
    }
  }

  return byteLength;
};

/**
 * Keeps small charts inline in page context and replaces larger payloads with
 * a compact index that points Operator to the shared chart-data read tool.
 */
export const getOperatorChartDataAccess = (
  chartConfig: HighchartsWrapperProps["chartConfig"],
  chartId: string
): OperatorChartDataAccess | undefined => {
  const data = getOperatorTimeSeriesData(chartConfig);

  if (!data) {
    return undefined;
  }

  const serializedData = JSON.stringify(data);
  if (getUtf8ByteLength(serializedData) <= maxInlineOperatorChartDescriptionBytes) {
    return {
      data,
      description: serializedData,
      requiresTool: false,
    };
  }

  const seriesSummary = data.series.map(({ name, type, data: seriesData }) => ({
    name,
    type,
    pointCount: seriesData.length,
  }));
  const summary: OperatorTimeSeriesSummary = {
    type: "time_series_summary",
    id: chartId,
    xAxis: data.xAxis,
    seriesCount: data.series.length,
    totalPointCount: data.series.reduce((total, { data: seriesData }) => total + seriesData.length, 0),
    series: seriesSummary,
    hasMoreSeries: false,
    readWith: {
      tool: "get_chart_data",
      toolEntityId: chartId,
    },
  };
  let serializedSummary = JSON.stringify(summary);

  while (
    getUtf8ByteLength(serializedSummary) > maxInlineOperatorChartDescriptionBytes &&
    summary.series.length > 0
  ) {
    summary.series.pop();
    summary.hasMoreSeries = true;
    serializedSummary = JSON.stringify(summary);
  }

  return {
    data,
    description: serializedSummary,
    requiresTool: true,
  };
};
